import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Fab,
  Zoom,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Chip,
  Tooltip,
  InputBase,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

type ChatRole = 'user' | 'assistant';

interface ChatMessage {
  role: ChatRole;
  content: string;
}

const DEFAULT_GREETING =
  'Olá! Sou a Assistente Pooh. Posso ajudar com navegação, Clínica, Inbox, Financeiro e boas práticas no dia a dia. Em que posso ajudar?';

/** Logo oficial (`public/10.png`). */
const ASSISTANT_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/10.png`;

function AssistantBrandLogo({
  size = 40,
  rounded = false,
}: {
  size?: number;
  /** Círculo com fundo claro útil quando o PNG tem margem escura (ex.: FAB). */
  rounded?: boolean;
}) {
  const inner = (
    <Box
      component="img"
      src={ASSISTANT_LOGO_SRC}
      alt=""
      sx={{
        width: rounded ? size - Math.max(6, Math.round(size * 0.14)) : size,
        height: rounded ? size - Math.max(6, Math.round(size * 0.14)) : size,
        objectFit: 'contain',
        flexShrink: 0,
        display: 'block',
      }}
    />
  );

  if (!rounded) return inner;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        boxShadow: 'inset 0 0 0 1px rgba(226,232,240,0.9)',
        overflow: 'hidden',
      }}
    >
      {inner}
    </Box>
  );
}

/** Linha tipo ECG para fundo discreto */
function EkgBackdrop() {
  return (
    <Box
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 56,
        opacity: 0.14,
        pointerEvents: 'none',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom center',
        backgroundSize: '100% 56px',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 48' preserveAspectRatio='none'%3E%3Cpath fill='none' stroke='%2394a3b8' stroke-width='1.2' d='M0 28 L40 28 L52 12 L64 36 L76 20 L88 28 L130 28 L142 10 L154 38 L166 22 L178 28 L240 28 L252 14 L264 34 L276 24 L288 28 L340 28 L352 16 L364 32 L376 26 L400 28'/%3E%3C/svg%3E")`,
      }}
    />
  );
}

const VirtualAssistantWidget: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: DEFAULT_GREETING },
  ]);
  const listEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    listEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, open]);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const res = await axios.post(getApiUrl('/support/assistant/chat'), {
        messages: nextMessages.map((m) => ({ role: m.role, content: m.content })),
        clientContext: {
          pathname: location.pathname,
          role: user?.role || '',
          unitId: user?.current_unit?.id || '',
        },
      });

      const reply = res.data?.data?.reply as string | undefined;
      const citations = (res.data?.data?.citations as string[]) || [];

      let assistantText = reply || 'Sem resposta no momento.';
      if (citations.length > 0) {
        assistantText += `\n\n— Fontes na base interna: ${citations.slice(0, 6).join('; ')}`;
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: assistantText }]);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Não foi possível contactar a assistente.';
      setError(String(msg));
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'Não consegui obter resposta agora. Se o problema persistir, peça ao administrador da conta para confirmar se o servidor está com a chave de IA configurada e a API acessível.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, messages, location.pathname, user]);

  if (!user) return null;

  /** Inbox tem barra de composição fixa em baixo — levantar FAB e painel para não tapar enviar. */
  const isInboxRoute = /^\/inbox(\/|$)/.test(location.pathname);
  const inboxBottomLiftPx = isInboxRoute ? 52 : 0;
  const fabBottom = `calc(env(safe-area-inset-bottom, 0px) + ${16 + inboxBottomLiftPx}px)`;
  const panelBottom = `calc(env(safe-area-inset-bottom, 0px) + ${88 + inboxBottomLiftPx}px)`;

  return (
    <>
      <Zoom in>
        <Tooltip title="Abrir chat com a Assistente Pooh" placement="left">
          <Fab
          aria-label="Abrir chat da Assistente Pooh"
          onClick={() => setOpen((v) => !v)}
          sx={{
            position: 'fixed',
            right: `calc(env(safe-area-inset-right, 0px) + 16px)`,
            bottom: fabBottom,
            zIndex: (theme) => theme.zIndex.modal + 2,
            width: 58,
            height: 58,
            bgcolor: '#14b8a6',
            color: '#fff',
            boxShadow: '0 10px 32px rgba(20,184,166,0.42), 0 4px 12px rgba(59,130,246,0.18)',
            overflow: 'hidden',
            '&:hover': {
              boxShadow: '0 12px 38px rgba(20,184,166,0.5)',
              bgcolor: '#0d9488',
            },
          }}
        >
          {open ? (
            <CloseIcon sx={{ color: '#fff', fontSize: 28 }} />
          ) : (
            <ChatIcon sx={{ fontSize: 30 }} aria-hidden />
          )}
        </Fab>
        </Tooltip>
      </Zoom>

      {open && (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            right: `calc(env(safe-area-inset-right, 0px) + 16px)`,
            bottom: panelBottom,
            width: { xs: 'calc(100vw - 24px)', sm: 460, md: 500 },
            maxWidth: { xs: 'min(100vw - 24px, 560px)', sm: 520 },
            height: { xs: 'min(82vh, 720px)', sm: 620, md: 660 },
            minHeight: { xs: 'min(82vh, 720px)', sm: 620 },
            zIndex: (theme) => theme.zIndex.modal + 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '22px',
            border: '1px solid rgba(255,255,255,0.65)',
            boxShadow: '0 24px 56px rgba(15,118,110,0.18), 0 8px 24px rgba(59,130,246,0.12)',
            background: 'linear-gradient(165deg, rgba(240,253,250,0.98) 0%, rgba(248,250,252,0.99) 45%, rgba(239,246,255,0.95) 100%)',
            backdropFilter: 'blur(14px)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.35,
              background:
                'linear-gradient(165deg, #ffffff 0%, #fafdfb 42%, rgba(240,253,250,0.65) 100%)',
              borderBottom: '1px solid rgba(226,232,240,0.85)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <AssistantBrandLogo size={44} rounded />
            <Typography
              sx={{
                flex: 1,
                fontWeight: 700,
                fontSize: '1.02rem',
                color: '#0f172a',
                letterSpacing: '-0.02em',
              }}
            >
              Assistente Pooh
            </Typography>
            <Tooltip title="Fechar">
              <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'rgba(15,23,42,0.75)' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Mensagens */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 1.75,
              py: 1.5,
              position: 'relative',
              bgcolor: 'rgba(248,250,252,0.65)',
            }}
          >
            <EkgBackdrop />
            {messages.map((m, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-end',
                  gap: 0.75,
                  mb: 1.35,
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {m.role === 'assistant' && (
                  <Box sx={{ mb: 0.25 }}>
                    <AssistantBrandLogo size={30} rounded />
                  </Box>
                )}
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: m.role === 'user' ? '88%' : 'calc(88% - 32px)',
                    px: 1.35,
                    py: 1,
                    borderRadius: m.role === 'user' ? '16px 16px 6px 16px' : '16px 16px 16px 6px',
                    ...(m.role === 'user'
                      ? {
                          background:
                            'linear-gradient(145deg, #0f766e 0%, #14b8a6 52%, #0d9488 100%)',
                          border: '1px solid rgba(255,255,255,0.22)',
                          boxShadow: '0 6px 18px rgba(15,118,110,0.35)',
                        }
                      : {
                          bgcolor: '#eff6ff',
                          border: '1px solid rgba(59,130,246,0.28)',
                          boxShadow: '0 4px 14px rgba(30,64,175,0.08)',
                        }),
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.875rem',
                      lineHeight: 1.55,
                      color: m.role === 'user' ? '#f0fdfa' : '#1e293b',
                      fontWeight: m.role === 'user' ? 500 : 400,
                    }}
                  >
                    {m.content}
                  </Typography>
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.25, position: 'relative', zIndex: 1 }}>
                <CircularProgress size={24} sx={{ color: '#14b8a6' }} />
              </Box>
            )}
            {error && (
              <Chip
                label={error}
                color="warning"
                size="small"
                sx={{ mt: 0.5, maxWidth: '100%', position: 'relative', zIndex: 1 }}
              />
            )}
            <div ref={listEndRef} />
          </Box>

          {/* Input estilo pílula + enviar */}
          <Box sx={{ px: 1.75, pb: 1.25, pt: 0.5, bgcolor: 'rgba(255,255,255,0.45)', position: 'relative' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: '#fff',
                borderRadius: '999px',
                border: '1px solid rgba(226,232,240,0.95)',
                pl: 2,
                pr: 0.75,
                py: 0.35,
                boxShadow: '0 4px 16px rgba(15,23,42,0.05)',
              }}
            >
              <InputBase
                fullWidth
                placeholder="Escreva a sua pergunta…"
                value={input}
                disabled={loading}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void sendMessage();
                  }
                }}
                sx={{
                  fontSize: '0.875rem',
                  py: 0.75,
                  '& input::placeholder': { color: '#94a3b8', opacity: 1 },
                }}
              />
              <IconButton
                aria-label="Enviar"
                onClick={() => void sendMessage()}
                disabled={loading || !input.trim()}
                sx={{
                  width: 42,
                  height: 42,
                  bgcolor: '#14b8a6',
                  color: '#fff',
                  '&:hover': { bgcolor: '#0d9488' },
                  '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#fff' },
                }}
              >
                <SendIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            <Box
              sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
              }}
            >
              <AssistantBrandLogo size={18} rounded />
              <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', textAlign: 'center' }}>
                Powered by Pooh Saúde AI Core | v2.1
              </Typography>
            </Box>

            <AutoAwesomeIcon
              sx={{
                position: 'absolute',
                right: 10,
                bottom: 10,
                fontSize: 18,
                color: 'rgba(148,163,184,0.55)',
                pointerEvents: 'none',
              }}
            />
          </Box>
        </Paper>
      )}
    </>
  );
};

export default VirtualAssistantWidget;
